import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { MR_HOME } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import CameraCapture from '@/components/shared/CameraCapture';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  CheckCircle, MapPin, Clock, CalendarCheck, GasPump, Gift,
  Camera, SealCheck, Crosshair, ArrowRight, TrendUp, Wallet
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export default function MRHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [todayMarked, setTodayMarked] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [marking, setMarking] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selfieData, setSelfieData] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [visits, setVisits] = useState([]);

  const today = new Date().toISOString().split('T')[0];
  const todayFormatted = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  useEffect(() => {
    const month = today.slice(0, 7);
    Promise.all([
      api.getAttendance(month),
      api.getExpenses(month),
      api.getGifts(false),
      api.getVisits(),
    ]).then(([attRes, expRes, giftRes, visitRes]) => {
      setAttendance(attRes.data);
      const todayAtt = attRes.data.find(a => a.date === today);
      setTodayMarked(!!todayAtt);
      setTodayRecord(todayAtt);
      setExpenses(expRes.data);
      setGifts(giftRes.data);
      setVisits(visitRes.data);
    });
  }, [today]);

  const getGPS = async () => {
    setGpsLoading(true);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, enableHighAccuracy: true })
      );
      setGpsLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    } catch {
      setGpsLocation({ lat: 28.6139, lng: 77.2090 });
    }
    setGpsLoading(false);
  };

  const handleSelfieCapture = (dataUrl) => {
    setSelfieData(dataUrl);
    setShowCamera(false);
  };

  const markAttendance = async () => {
    setMarking(true);
    try {
      const loc = gpsLocation || { lat: 28.6139, lng: 77.2090 };
      let selfieRef = '';
      if (selfieData) {
        try {
          const { data } = await api.uploadPhoto(selfieData);
          selfieRef = data.photo_id;
        } catch {}
      }
      await api.markAttendance({
        date: today,
        check_in_time: new Date().toISOString(),
        latitude: loc.lat,
        longitude: loc.lng,
        selfie_url: selfieRef,
        status: 'present'
      });
      setTodayMarked(true);
    } catch (err) {
      console.error(err);
    }
    setMarking(false);
  };

  const presentDays = attendance.filter(a => a.status === 'present').length;
  const monthExpenses = expenses.reduce((s, e) => s + e.amount, 0);
  const pendingGifts = gifts.filter(g => !g.delivered).length;
  const todayVisits = visits.filter(v => v.visited_at?.startsWith(today)).length;

  const startAttendance = () => {
    getGPS();
    setShowCamera(true);
  };

  return (
    <div data-testid={MR_HOME.container} className="space-y-4">
      {/* Greeting Banner */}
      <div className="px-5 pt-5 pb-4" style={{ background: 'linear-gradient(180deg, #0A2540 0%, #0A2540 60%, #F9F9FA 100%)' }}>
        <p className="text-white/50 text-xs font-medium">{todayFormatted}</p>
        <h2 className="text-xl font-extrabold tracking-tight text-white mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Hello, {user?.name?.split(' ')[0]}
        </h2>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
            <TrendUp size={16} weight="duotone" style={{ color: '#34C759' }} />
            <p className="text-white text-lg font-bold mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{todayVisits}</p>
            <p className="text-[10px] text-white/50 font-medium">Visits Today</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <CalendarCheck size={16} weight="duotone" style={{ color: '#007AFF' }} />
            <p className="text-white text-lg font-bold mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{presentDays}</p>
            <p className="text-[10px] text-white/50 font-medium">Days Present</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <Wallet size={16} weight="duotone" style={{ color: '#FFCC00' }} />
            <p className="text-white text-lg font-bold mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{monthExpenses.toLocaleString()}</p>
            <p className="text-[10px] text-white/50 font-medium">Expenses</p>
          </div>
        </div>
      </div>

      <div className="px-4 space-y-4">
        {/* Attendance Card */}
        <div data-testid={MR_HOME.attendanceCard} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E2E4E9' }}>
          <div className="p-4" style={{ background: todayMarked ? '#F0FFF4' : '#FFFFFF' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: todayMarked ? '#34C75915' : '#007AFF15' }}>
                  {todayMarked ?
                    <SealCheck size={18} weight="fill" style={{ color: '#34C759' }} /> :
                    <Crosshair size={18} weight="duotone" style={{ color: '#007AFF' }} />
                  }
                </div>
                <div>
                  <h3 className="text-sm font-bold" style={{ color: '#0A2540', fontFamily: 'Manrope, sans-serif' }}>
                    {todayMarked ? 'Attendance Marked' : 'Mark Attendance'}
                  </h3>
                  <p className="text-[10px]" style={{ color: '#8A94A6' }}>
                    {todayMarked ? `Checked in at ${todayRecord?.check_in_time?.slice(11, 16) || '--:--'}` : 'Selfie + GPS verification required'}
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{
                  background: todayMarked ? '#34C759' : '#FF3B30',
                  color: 'white'
                }}>
                {todayMarked ? 'Present' : 'Pending'}
              </span>
            </div>

            {todayMarked ? (
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: '#34C75910' }}>
                <CheckCircle size={24} weight="fill" style={{ color: '#34C759' }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#34C759' }}>You're checked in for today</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#8A94A6' }}>GPS location verified</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Selfie Preview */}
                {selfieData && (
                  <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: '#F0F4F8' }}>
                    <img src={selfieData} alt="Selfie" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold" style={{ color: '#0A2540' }}>Selfie captured</p>
                      <p className="text-[10px]" style={{ color: '#8A94A6' }}>Ready to submit</p>
                    </div>
                    <button onClick={() => setSelfieData(null)} className="text-xs font-medium" style={{ color: '#007AFF' }}>Retake</button>
                  </div>
                )}

                {/* GPS Status */}
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: '#F0F4F8' }}>
                  <MapPin size={16} weight="duotone" style={{ color: gpsLocation ? '#34C759' : '#8A94A6' }} />
                  <span className="text-xs" style={{ color: gpsLocation ? '#34C759' : '#8A94A6' }}>
                    {gpsLoading ? 'Getting location...' : gpsLocation ? `Location: ${gpsLocation.lat.toFixed(4)}, ${gpsLocation.lng.toFixed(4)}` : 'GPS not captured yet'}
                  </span>
                </div>

                {!selfieData ? (
                  <Button
                    data-testid={MR_HOME.markAttendanceBtn}
                    onClick={startAttendance}
                    className="w-full h-12 text-sm font-bold rounded-xl"
                    style={{ background: '#007AFF', color: 'white' }}
                  >
                    <Camera size={18} className="mr-2" /> Take Selfie & Mark
                  </Button>
                ) : (
                  <Button
                    onClick={markAttendance}
                    disabled={marking}
                    className="w-full h-12 text-sm font-bold rounded-xl"
                    style={{ background: '#34C759', color: 'white' }}
                  >
                    {marking ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <><SealCheck size={18} className="mr-2" /> Confirm Attendance</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Action Cards */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: '#8A94A6' }}>Quick Actions</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Beat Plan', desc: "View today's route", icon: CalendarCheck, color: '#007AFF', bg: '#007AFF10', path: '/mr/beat', badge: null },
              { label: 'Fuel Expense', desc: 'Log reading', icon: GasPump, color: '#FFCC00', bg: '#FFCC0010', path: '/mr/expenses', badge: null },
              { label: 'Gifts', desc: `${pendingGifts} pending`, icon: Gift, color: '#FF3B30', bg: '#FF3B3010', path: '/mr/gifts', badge: pendingGifts > 0 ? pendingGifts : null },
              { label: 'Visit Log', desc: `${todayVisits} today`, icon: Clock, color: '#34C759', bg: '#34C75910', path: '/mr/visits', badge: null },
            ].map(item => {
              const Icon = item.icon;
              return (
                <button key={item.label} onClick={() => navigate(item.path)}
                  className="p-4 rounded-xl border text-left active:scale-[0.97] transition-transform relative"
                  style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                  {item.badge && (
                    <span className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ background: '#FF3B30' }}>{item.badge}</span>
                  )}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: item.bg }}>
                    <Icon size={22} weight="duotone" style={{ color: item.color }} />
                  </div>
                  <p className="text-sm font-bold" style={{ color: '#0A2540', fontFamily: 'Manrope, sans-serif' }}>{item.label}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#8A94A6' }}>{item.desc}</p>
                  <ArrowRight size={14} className="absolute bottom-4 right-4" style={{ color: '#E2E4E9' }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Monthly Attendance Calendar */}
        <div className="rounded-xl border p-4" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <h3 className="text-sm font-bold mb-3" style={{ color: '#0A2540', fontFamily: 'Manrope, sans-serif' }}>
            Attendance - {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="grid grid-cols-7 gap-1.5">
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <div key={i} className="text-center text-[9px] font-bold uppercase" style={{ color: '#8A94A6' }}>{d}</div>
            ))}
            {/* Get first day offset */}
            {Array.from({ length: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getDay() }, (_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() }, (_, i) => {
              const day = String(i + 1).padStart(2, '0');
              const dateStr = `${today.slice(0, 8)}${day}`;
              const att = attendance.find(a => a.date === dateStr);
              const isToday = dateStr === today;
              const isPast = new Date(dateStr) < new Date(today);
              return (
                <div key={i} className="aspect-square rounded-lg flex items-center justify-center text-[11px] font-semibold"
                  style={{
                    background: att?.status === 'present' ? '#34C759' : att?.status === 'half-day' ? '#FFCC00' : isToday ? '#007AFF' : isPast && !att ? '#FF3B3015' : '#F5F6F8',
                    color: (att || isToday) ? '#FFFFFF' : isPast && !att ? '#FF3B30' : '#8A94A6',
                  }}>
                  {i + 1}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 pt-3" style={{ borderTop: '1px solid #E2E4E9' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ background: '#34C759' }} />
              <span className="text-[10px]" style={{ color: '#8A94A6' }}>Present</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ background: '#FF3B3015', border: '1px solid #FF3B3040' }} />
              <span className="text-[10px]" style={{ color: '#8A94A6' }}>Absent</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ background: '#007AFF' }} />
              <span className="text-[10px]" style={{ color: '#8A94A6' }}>Today</span>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={setShowCamera}>
        <DialogContent className="max-w-sm p-4">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Take Attendance Selfie</DialogTitle>
          </DialogHeader>
          <CameraCapture
            facing="user"
            label="Position your face in the frame"
            onCapture={handleSelfieCapture}
            onCancel={() => setShowCamera(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
